import { BASE_URL } from "../../config";

import {
    ADD_ORDER,
    REMOVE_ORDER,
    UPDATE_ORDERS,
    GET_ALL_ORDERS,
    FORCE_UPDATE_ORDER,
    UPDATE_ORDER,
} from "./order.actionsTypes";
import axios from "axios";

import delivery from "../../components/delivery/delivery";
import Notifier from "../../components/message/Notifier";
import { ERRORS, GIFT_CARD_STATUSES } from "../../config/constants";
import { updateGiftCards } from "../GiftCard/giftCard.actions";

// buffer incoming socket updates so we don't dispatch for every single event
// this reduces the number of times the reducer has to run and avoids
// excessive re‑sorting of the orders array.
let _pendingOrderUpdates = [];
let _flushTimer = null;

const _flushPendingOrders = (dispatch) => {
    if (_pendingOrderUpdates.length) {
        const batch = _pendingOrderUpdates;
        _pendingOrderUpdates = [];
        dispatch(updateOrders(batch));
    }
};

const _scheduleFlush = (dispatch) => {
    if (_flushTimer) {
        return;
    }
    _flushTimer = setTimeout(() => {
        _flushTimer = null;
        _flushPendingOrders(dispatch);
    }, 100); // throttle interval can be tuned (100ms is a good starting point)
};

const registerSessionListeners = (dispatch) => {
    if (delivery.ordersListenersRegistered) {
        return;
    }

    delivery.orderAdded((order) => {
        dispatch(getOrder(order, addOrder));
    });

    delivery.orderUpdated((order) => {
        if (order.forceUpdate) {
            dispatch(getOrder(order, forceUpdateOrder));
        } else {
            _pendingOrderUpdates.push(order);
            _scheduleFlush(dispatch);
        }
    });

    delivery.disconnected((reason) => {
        if (-1 === ERRORS.forced_close.reason.indexOf(reason)) {
            Notifier.error(
                ERRORS.network_error.message,
                ERRORS.network_error.title,
                true,
            );
        }
    });

    delivery.ordersListenersRegistered = true;
};

export const assignDistributor = (order) => {
    const session = delivery.getSession();
    session.emit("delivery:assign", order);
    return () => {
    };
};

export const updateReceiverLocation = (location, address, orderId, cb) => {
    return () => {
        axios
            .put(`${BASE_URL}order/updateReceiverLocation/${orderId}`, {
                location,
                address
            })
            .then((response) => {
                if (response.data && response.data.success) {
                    cb(true);
                } else {
                    cb(false);
                }
            })
            .catch((error) => {
                console.error("Error updating location:", error);
                cb(false);
            });
    };
};

export const changeEta = (order, setUserUpdated) => {
    return (dispatch) => {
        axios
            .put(`${BASE_URL}order/eta/${order.orderId}`, order)
            .then((response) => {
                if (response.data) {
                    if ("function" === typeof setUserUpdated) {
                        setUserUpdated(response.data);
                        dispatch(updateOrder(response.data));
                    }
                    Notifier.success(
                        "_order_successfully_updated_",
                        "_order_updated_",
                    );
                }
            });
    };
};

export const updateOrders = (orders) => {
    return {
        type: UPDATE_ORDERS,
        orders,
    };
};

export const updateOrder = (order) => {
    return {
        type: UPDATE_ORDER,
        order,
    };
};

export const addOrder = (order) => {
    return {
        type: ADD_ORDER,
        order,
    };
};

export const addOrderByApi = (order, callback) => {
    return (dispatch) => {
        axios.post(`${BASE_URL}order/bulk`, order).then((response) => {
            callback(response.data);
        });
    };
};

export const removeOrder = (id) => {
    return {
        type: REMOVE_ORDER,
        id,
    };
};

export const getOrder = (order, callback) => {
    return (dispatch) => {
        axios.get(`${BASE_URL}v2/order/${order.id}`).then((response) => {
            if (response.data && response.data.order) {
                if (order.hasOwnProperty("blink")) {
                    response.data.order.blink = order.blink;
                }
                dispatch(callback(response.data.order));
            }
        });
    };
};

export const getShop = (orderId, partner, callback) => {
    return (dispatch) => {
        axios.get(`${BASE_URL}order/${orderId}/${partner}`).then((response) => {
            if (response.data) {
                callback(response.data);
            }
        });
    };
};

export const getAllOrders = () => {
    return (dispatch) => {
        axios.get(`${BASE_URL}v2/order`).then((response) => { // use v2 instead of /order
            registerSessionListeners(dispatch);
            if (response.data) {
                dispatch({
                    type: GET_ALL_ORDERS,
                    orders: response.data,
                });
            }
        });
    };
};

export const getOrdersByPhone = (phone, callback) => {
    return (dispatch) => {
        axios.get(`${BASE_URL}order?phone=${phone}`).then(function (response) {
            callback(response.data);
        });
    };
};

export const forceUpdateOrder = (order) => {
    return {
        type: FORCE_UPDATE_ORDER,
        order,
    };
};

export const cancelOrderByApi = (id, order) => {
    return (dispatch) => {
        axios.put(`${BASE_URL}order/cancel/${id}`, order).then((response) => {
            if (response.data) {
                dispatch(updateOrder(response.data));
                Notifier.success(
                    "_order_successfully_updated_",
                    "_order_updated_",
                );
            }
        });
    };
};

export const getOrderById = (id, callback) => {
    return (dispatch) => {
        if (!id) {
            Notifier.warning("_select_order_id_");
            return;
        }
        axios.get(`${BASE_URL}order/q?id=${id}`).then((response) => {
            if (response.data && "function" === typeof callback) {
                callback(response.data);
            }
        });
    };
};

export const restoreOrderByApi = (id) => {
    return (dispatch) => {
        axios.put(`${BASE_URL}order/restore/${id}`).then((response) => {
            if (response.data) {
                dispatch(updateOrder(response.data));
                Notifier.success(
                    "_order_successfully_updated_",
                    "_order_updated_",
                );
            }
        });
    };
};

export const addOrderByAMQP = (body, callback) => {
    return () => {
        axios.post(`${BASE_URL}order/createByAMQP`, body).then((response) => {
            callback(response.data);
        });
    };
};

export const updateOrderByApi = (order, setUserUpdated, onError) => {
    const apiURL = order.bookId
        ? `${BASE_URL}order/books/${order.id}`
        : `${BASE_URL}order/${order.id}`;
    const handleError = typeof onError === "function" ? onError : null;
    const getBackendMessage = (error) => {
        return (
            error?.response?.data?.messages?.[0]?.text ||
            error?.response?.data?.message ||
            error?.response?.data?.error ||
            error?.message ||
            ""
        );
    };
    return (dispatch) => {
        axios.put(apiURL, order).then((response) => {
            if (response.data) {
                if ("function" === typeof setUserUpdated) {
                    setUserUpdated(response.data);
                }
                dispatch(updateOrder(response.data));
                if (
                    response.data.giftCardCodes &&
                    response.data.giftCardCodes.length &&
                    response.data.customer.customerId &&
                    response.data.status == 1
                ) {
                    const customerIdArr =
                        response.data.customer.customerId.split("_");
                    const customerId = customerIdArr[customerIdArr.length - 1];
                    updateGiftCards({
                        cards: response.data.giftCardCodes,
                        orderId: response.data.orderId,
                        customerId,
                        status: GIFT_CARD_STATUSES.RESERVED.statusId,
                    });
                }

                Notifier.success(
                    "_order_successfully_updated_",
                    "_order_updated_",
                );
                // the previous implementation invoked getAllOrders() without
                // dispatching it and pulled the entire list on every update –
                // that defeats the purpose of incremental updates and was
                // a major source of IO and reducer work. keep the incremental
                // dispatch only.
                // dispatch(getAllOrders());
            }
        }).catch((error) => {
            const backendMessage = getBackendMessage(error);
            const errorMsg = error.response?.data?.messages?.[0]?.text;

            if (errorMsg === "sysERR_INVALID_GIFT_CARD_CODES") {
                Notifier.error(
                    "Պահպանումը ձախողվեց՝ ոչ վավեր նվեր քարտի կոդ",
                    "_order_update_failed_",
                    true
                );
            } else if (backendMessage) {
                // Surface backend validation (e.g. missing/invalid delivery
                // amount reason from OMSNEW-1148) instead of failing silently.
                Notifier.error(backendMessage, "_order_update_failed_");
            } else {
                Notifier.error(
                    "_order_update_failed_",
                    "_error_occurred_"
                );
            }
            if (handleError) {
                handleError(backendMessage || errorMsg || "");
            }
        });
    };
};
